import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, BookOpen, ArrowRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { api } from "@/api";
import { useLang, tr } from "@/i18n";

const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export const BlogSection = () => {
    const [posts, setPosts] = useState([]);
    const [active, setActive] = useState(null);
    const { lang } = useLang();
    const t = (s) => tr(lang, s);

    useEffect(() => {
        api.get("/posts").then(({ data }) => setPosts(data)).catch(() => {});
    }, []);

    if (!posts.length) return null;

    return (
        <section id="blog" data-testid="blog-section" className="py-20 lg:py-28 bg-[#F4EDE2]">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Reveal>
                    <Overline>{t("Chapter 06 — Gyaan & Guidance")}</Overline>
                    <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl text-[#241D19] tracking-tight">
                        {t("Ritual Guidance & Blog")}
                    </h2>
                </Reveal>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
                    {posts.map((p, i) => (
                        <Reveal key={p.id} delay={(i % 3) * 0.1} className="h-full">
                            <article
                                data-testid={`blog-card-${p.id}`}
                                className="h-full bg-white rounded-2xl overflow-hidden border border-[#E6DBCF] shadow-[0_2px_16px_rgba(36,29,25,0.05)] hover:shadow-[0_12px_36px_rgba(156,55,11,0.12)] transition-shadow duration-500 flex flex-col"
                            >
                                {p.image && (
                                    <img src={p.image} alt={p.title} loading="lazy" className="w-full h-48 object-cover" />
                                )}
                                <div className="p-6 flex flex-col flex-1">
                                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#C85A17]">{fmtDate(p.created_at)}</p>
                                    <h3 className="mt-2 font-serif-alt font-semibold text-xl text-[#241D19] leading-snug">{p.title}</h3>
                                    <p className="mt-2 text-sm text-[#6E6259] leading-relaxed line-clamp-3 flex-1">{p.body}</p>
                                    <button
                                        data-testid={`blog-read-${p.id}`}
                                        onClick={() => setActive(p)}
                                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#C85A17] hover:text-[#9C370B] transition-colors group"
                                    >
                                        <BookOpen size={15} /> {t("Read Article")}
                                        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                                    </button>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {active && (
                    <motion.div
                        data-testid="blog-reader-modal"
                        className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setActive(null)} />
                        <motion.div
                            initial={{ y: 60, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 60, opacity: 0 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="relative bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto"
                        >
                            {active.image && (
                                <img src={active.image} alt={active.title} className="w-full h-56 object-cover" />
                            )}
                            <button
                                onClick={() => setActive(null)}
                                data-testid="blog-reader-close-button"
                                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center text-[#241D19] hover:bg-white"
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                            <div className="p-6 sm:p-10">
                                <p className="text-[11px] uppercase tracking-[0.2em] text-[#C85A17]">{fmtDate(active.created_at)}</p>
                                <h3 className="mt-2 font-serif-alt font-semibold text-2xl sm:text-3xl text-[#241D19] leading-snug">
                                    {active.title}
                                </h3>
                                <p className="mt-5 text-[#4A4039] leading-relaxed whitespace-pre-wrap">{active.body}</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
